import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PostCard from '../components/PostCard';
import SkeletonPostCard from '../components/SkeletonPostCard';
import { toast } from 'sonner';
import { API_URL } from '../config';

const API = API_URL;

const SavedPostsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchSavedPosts();
  }, []);

  const fetchSavedPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/saved-posts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      } else {
        toast.error('Failed to load saved posts');
      }
    } catch (error) {
      console.error('Error fetching saved posts:', error);
      toast.error('Error loading posts');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (postId, voteType) => {
    const token = localStorage.getItem('token');
    
    try {
      if (voteType === null) {
        await fetch(`${API}/posts/${postId}/vote`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await fetch(`${API}/posts/${postId}/vote?vote_type=${voteType}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      fetchSavedPosts();
    } catch (error) {
      console.error('Vote error:', error);
      throw error;
    }
  };

  const handleUnsave = async (postId) => {
    const token = localStorage.getItem('token');
    
    try {
      await fetch(`${API}/posts/${postId}/save`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post removed from saved');
    } catch (error) {
      console.error('Unsave error:', error);
      throw error;
    }
  };

  const handleSave = async (postId) => {
    // Not used here but required by PostCard
  };

  const handleUnlock = (post) => {
    toast.info('Unlock feature');
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b"
        style={{ background: '#121212', borderColor: '#2A2A2A' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-white text-xl font-bold">Saved Posts</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-6">
            <SkeletonPostCard />
            <SkeletonPostCard />
            <SkeletonPostCard />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-4">No saved posts yet</p>
            <p className="text-gray-500 text-sm">Posts you save will appear here</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <PostCard
                key={post.post_id}
                post={post}
                onUnlock={handleUnlock}
                onVote={handleVote}
                onSave={handleSave}
                onUnsave={handleUnsave}
                user={user}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPostsPage;
